import { Carousel } from "@mantine/carousel";
import {
  Card,
  Center,
  Flex,
  Group,
  Image,
  Progress,
  RingProgress,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import Autoplay from "embla-carousel-autoplay";
import { useRef } from "react";
import "./DidYouKnow.css";

const funFacts: Array<{
  title: string;
  description: string;
  visualization: {
    type: "progress" | "ring" | "stat";
    data: any;
  };
  country?: string;
  flag?: string;
}> = [
  {
    title: "Denmark leads in digital government services",
    description:
      "98% of Danish citizens use digital government services, the highest rate in the EU. This digital transformation has improved efficiency and citizen satisfaction.",
    visualization: {
      type: "progress",
      data: { value: 98, label: "Digital adoption rate" },
    },
    country: "Denmark",
    flag: "/europafacts/static/images/flags/denmark.gif",
  },
  {
    title: "Renewable energy growth in Europe",
    description:
      "In 2023, renewable energy sources accounted for 44% of electricity generation in the EU, up from 32% in 2015. This represents a significant shift towards sustainable energy.",
    visualization: {
      type: "ring",
      data: { value: 44, label: "Renewable energy share" },
    },
  },
  {
    title: "Urban population trends",
    description:
      "Over 75% of Europeans live in urban areas, with cities generating 85% of the EU's GDP. Understanding urban data is crucial for policy and planning.",
    visualization: {
      type: "progress",
      data: { value: 75, label: "Urban population" },
    },
  },
];

export function DidYouKnow() {
  const autoplay = useRef(Autoplay({ delay: 5000, stopOnInteraction: false }));

  const renderVisualization = (fact: {
    title: string;
    description: string;
    visualization: {
      type: "progress" | "ring" | "stat";
      data: any;
    };
    country?: string;
    flag?: string;
  }) => {
    switch (fact.visualization.type) {
      case "progress":
        return (
          <Stack gap="xs" flex={1}>
            <Text size="sm" fw={500} c="gray.9">
              {fact.visualization.data.label}
            </Text>
            <Progress
              value={fact.visualization.data.value}
              size="xl"
              radius="md"
              color="eu_flag"
            />
            <Text size="xl" fw={700} c="eu_flag.9">
              {fact.visualization.data.value}%
            </Text>
          </Stack>
        );
      case "ring":
        return (
          <Center>
            <RingProgress
              size={120}
              thickness={12}
              sections={[
                {
                  value: fact.visualization.data.value,
                  color: "var(--mantine-color-eu_flag-filled)",
                },
              ]}
              label={
                <Center>
                  <Text size="xl" fw={700} c="eu_flag.9">
                    {fact.visualization.data.value}%
                  </Text>
                </Center>
              }
            />
          </Center>
        );
      default:
        return null;
    }
  };

  const slides = funFacts.map((fact) => (
    <Carousel.Slide key={fact.title}>
      <Stack gap="lg" style={{ minHeight: 200 }}>
        <Flex direction="column" gap={8}>
          {fact.country && (
            <Group gap={6} align="center">
              {fact.flag && (
                <Image
                  src={fact.flag}
                  alt={`${fact.country} flag`}
                  w={20}
                  h={15}
                  fit="cover"
                  style={{
                    borderRadius: "1px",
                  }}
                />
              )}
              <Text size="sm" c="gray.9" fw={600}>
                {fact.country}
              </Text>
            </Group>
          )}
          <Text fw={600} fz={20}>
            {fact.title}
          </Text>
        </Flex>
        <Text c="gray.9" fz={15} lh={1.6}>
          {fact.description}
        </Text>
        {renderVisualization(fact)}
      </Stack>
    </Carousel.Slide>
  ));

  return (
    <Stack gap="xl">
      <Group gap="sm" align="center">
        <Title order={2} fw={600} fz={28}>
          Did You Know?
        </Title>
      </Group>
      <Card p="xl" shadow="lg" radius="md">
        <Carousel
          withControls={false}
          slideSize="100%"
          slideGap={0}
          emblaOptions={{
            align: "start",
            loop: true,
          }}
          plugins={[autoplay.current]}
          onMouseEnter={() => autoplay.current.stop()}
          onMouseLeave={() => autoplay.current.play()}
          className="did-you-know-carousel"
        >
          {slides}
        </Carousel>
      </Card>
    </Stack>
  );
}
